
import OrderActivity from '@/modules/order/common/classes/OrderActivity.class';
import { orderModule } from '@/modules/order/orderModule.vuex-module';
import { Options, Vue } from 'vue-class-component';
import { InjectReactive, ProvideReactive } from 'vue-property-decorator';
import { Time } from '@/common/models/Time.model';
import { EntityHierarchy } from '@/common/models/EntityHierarchy.model';
import { EntityHierarchyRate } from '@/common/models/EntityHierarchyRate.model';
import moment from 'moment';
import DateSection from '@/modules/order/shop/components/add/experience/sections/date-section.component.vue';
import { clientModule } from '@/modules/client/client.vuex-module';

/**
 * Customer self-swap: choose one of Activity A's configured swap targets, then a date and time for the same guests.
 * The guests are carried over by rate global identifier (GlobalId), which the operator sets to match on both activities.
 */
@Options({
	name: 'TimeSwapView',
	components: { DateSection },
})
export default class TimeSwapView extends Vue {
	@InjectReactive() time?: Time;
	@ProvideReactive() activeSection: 'dates' | 'rates' | 'times' | 'addons' | 'discounts' | 'none' = 'dates';
	@ProvideReactive() activity: OrderActivity | null = null;
	loading = false;
	error: string | null = null;

	get source(): OrderActivity | null {
		return orderModule.openActivity;
	}
	get targets(): EntityHierarchy[] {
		const keys = this.source?.activity.SwapTargetEntityHierarchyKeys ?? [];
		return clientModule.locationActivities.filter((a) => keys.includes(a.EntityHierarchyKey));
	}
	get activityDate(): moment.Moment | null {
		return this.time ? moment(this.time.Appointment.Time) : null;
	}
	get activityDateString(): string {
		if (!this.activityDate) return '';
		return this.time?.AppointmentTypeKey == 1 ? this.activityDate.format('dddd, MMMM Do, YYYY [at] h:mma') : this.activityDate.format('dddd, MMMM Do, YYYY');
	}
	get phoneNumber(): string | undefined {
		return clientModule.location?.PublicPhoneNumber;
	}
	get sourceRates(): EntityHierarchyRate[] {
		return this.time?.Rates.filter((r) => r.Participants > 0) ?? [];
	}
	get requiredParticipants(): number {
		return this.sourceRates.filter((r) => !r.IsPrivateTourRate).reduce((sum, r) => sum + r.Participants, 0);
	}
	get rates(): string {
		return this.sourceRates.map((r) => r.Name + ' x ' + r.Participants).join(', ');
	}
	hasCapacity(t: Time): boolean {
		if (this.requiredParticipants <= 0) return true;
		return t.TotalAvailable >= this.requiredParticipants;
	}
	priceClass(t: Time): { increase?: boolean; decrease?: boolean } {
		const current = this.time?.Cost ?? 0;
		if (current > t.SubTotal) return { decrease: true };
		if (current < t.SubTotal) return { increase: true };
		return {};
	}
	/**
	 * Carries the booked guests onto the target's rates by matching rate global identifiers (e.g. Adult of A and Adult of
	 * B both set to 1). Returns false when some guests have no matching rate, in which case the swap has to be handled by
	 * staff.
	 */
	static mapGuests(sourceRates: EntityHierarchyRate[], target: EntityHierarchy): boolean {
		const publicRates = target.Rates.filter((r) => !r.IsPrivateTourRate && !r.InternalUseOnly && !r.IsMemberOnly);
		target.Rates.forEach((r) => (r.Participants = 0));
		const total = sourceRates.reduce((sum, r) => sum + r.Participants, 0);
		let mapped = 0;
		sourceRates.forEach((src) => {
			const match = src.GlobalId ? publicRates.find((r) => r.GlobalId === src.GlobalId) : undefined;
			if (match) {
				match.Participants += src.Participants;
				mapped += src.Participants;
			}
		});
		return mapped === total && total > 0;
	}
	/** Best-effort copy of Activity A's activity-level custom-question answers onto the target, matched by question. */
	static copyAnswers(source: EntityHierarchy, target: EntityHierarchy): void {
		(target.CustomFields ?? []).forEach((field) => {
			const answered = (source.CustomFields ?? []).find((f) => f.ClientLocationCustomFieldKey === field.ClientLocationCustomFieldKey);
			if (!answered) return;
			Object.keys(answered)
				.filter((k) => k.startsWith('Response_'))
				.forEach((k) => ((field as unknown as Record<string, unknown>)[k] = (answered as unknown as Record<string, unknown>)[k]));
		});
	}
	selectTarget(target: EntityHierarchy): void {
		this.error = null;
		const copy: EntityHierarchy = JSON.parse(JSON.stringify(target));
		copy.SelectedTimes = [];
		copy.AppointmentDate = this.time?.AppointmentDate ?? copy.AppointmentDate;
		if (!TimeSwapView.mapGuests(this.sourceRates, copy)) {
			this.activity = null;
			this.error = `The guests on this booking can't be moved to ${target.Name} online. Please call us at ${this.phoneNumber} to make this change.`;
			return;
		}
		if (this.source) TimeSwapView.copyAnswers(this.source.activity, copy);
		const activity = new OrderActivity(copy);
		activity.setDate(this.activityDate?.toDate() ?? new Date());
		this.activity = activity;
		orderModule.swapTarget = activity;
		this.loading = true;
		activity.getBookTimes().finally(() => (this.loading = false));
	}
	book(t: Time): void {
		if (!this.activity || !this.hasCapacity(t)) return;
		this.activity.pendingTime = t;
		this.$router.push({ name: 'OrderTimeSwapConfirm' });
	}
	mounted(): void {
		orderModule.isEditing = true;
		if (this.targets.length === 1) this.selectTarget(this.targets[0]);
	}
}
